import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { TranslateService } from '@ngx-translate/core';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { TenantUser } from 'src/app/models/tenantUser';
import { ToastGlobalService } from 'src/app/layout/toast-global/toast-global.service';
import { IssuesService } from './issues.service';
import { IssueDto } from './models/issue';
import { DispositionRequest } from './models/disposition';
import { WriteStatus, WriteStatusMap, WriteStatuses } from './models/issue-view';

// Reconcile backoff after a successful 202 write (the change applies async).
const RECONCILE_DELAYS_MS = [500, 1000, 2000];

// How long a fetched list answers later callers before the next one goes to the server.
const CACHE_TTL_MS = 60_000;

// App-lifetime store for per-vehicle issue lists + async-write reconciliation.
// The widget and drawer both read/write through here so a mutation in one is
// reflected in the other. Optimistic update -> 202 POST/DELETE -> refetch +
// reconcile with backoff; on HTTP failure the optimistic change rolls back.
@Injectable({ providedIn: 'root' })
export class IssuesStateService {
  @SelectSnapshot(TenantSessionState.getCurrentTenantUsers) private tenantUsers!: TenantUser[];

  private readonly _byAsset = new Map<string, BehaviorSubject<IssueDto[]>>();
  private readonly _loadingByAsset = new Map<string, BehaviorSubject<boolean>>();
  private readonly _errorByAsset = new Map<string, BehaviorSubject<string | null>>();
  private readonly _writeStatus = new BehaviorSubject<WriteStatusMap>({});

  // issueId -> assetId, so a write can find the list to reconcile.
  private readonly _assetByIssue = new Map<string, string>();
  // When each asset's list was last fetched. A later caller reads the cache instead of
  // starting the fetch again, until the entry ages out — see load().
  private readonly loadedAtMs = new Map<string, number>();

  // Fired on tenant switch / sign-out to cancel in-flight reconciles.
  private readonly _cancel$ = new Subject<void>();

  constructor(
    private readonly issues: IssuesService,
    private readonly toast: ToastGlobalService,
    private readonly translate: TranslateService,
  ) {}

  // --- Selectors -----------------------------------------------------------

  issuesFor(assetId: string): Observable<IssueDto[]> {
    return this.assetSubject(assetId).asObservable();
  }

  loadingFor(assetId: string): Observable<boolean> {
    return this.loadingSubject(assetId).asObservable();
  }

  errorFor(assetId: string): Observable<string | null> {
    return this.errorSubject(assetId).asObservable();
  }

  writeStatusFor(issueId: string): Observable<WriteStatus> {
    return this._writeStatus.pipe(
      map(m => m[issueId] ?? WriteStatuses.Idle),
      distinctUntilChanged(),
    );
  }

  snapshotFor(assetId: string): IssueDto[] {
    return this.assetSubject(assetId).value;
  }

  // Resolve a user id to a display name from the tenant user list.
  resolveUserName(userId: string): string | null {
    const u = (this.tenantUsers ?? []).find(x => x.id === userId);
    if (!u) return null;
    return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || null;
  }

  // --- Loading -------------------------------------------------------------

  // `force` refetches an asset already in the cache. Without it a second caller adopts
  // what the first one fetched.
  //
  // The guard is not an optimisation. A host that hides its content while this flag is
  // true — the vehicle widget swaps its rows for skeletons — destroys any child inside
  // that content, and a child which asks for a load as it initialises then re-enters
  // here the moment it is rebuilt. That is a cycle with no end: load, hide, destroy,
  // finish, show, load. Answering a caller from the cache breaks it, and it also stops
  // the widget and the detail fetching the same list twice on one open.
  //
  // The entry ages out so the cache cannot answer forever. Without that, a screen opened
  // once serves the same snapshot for the rest of the session, and an Issue another
  // operator disposed stays on it. The window only has to outlast the cycle above, which
  // completes in under a second.
  load(assetId: string, force = false): void {
    if (!assetId) return;
    if (!force && this.isFresh(assetId)) return;

    const loading = this.loadingSubject(assetId);
    const error = this.errorSubject(assetId);
    loading.next(true);
    error.next(null);

    this.issues.listByAsset(assetId).pipe(
      take(1),
      takeUntil(this._cancel$),
    ).subscribe({
      next: list => {
        this.setCache(assetId, list ?? []);
        this.loadedAtMs.set(assetId, Date.now());
        loading.next(false);
      },
      error: err => {
        error.next(this.extractMessage(err));
        loading.next(false);
      },
    });
  }

  private isFresh(assetId: string): boolean {
    const at = this.loadedAtMs.get(assetId);
    return at !== undefined && Date.now() - at < CACHE_TTL_MS;
  }

  // --- Writes (optimistic + reconcile) -------------------------------------

  closeIssue(assetId: string, issueId: string, request: DispositionRequest): void {
    const prev = this.snapshotFor(assetId);
    // Optimistically drop the issue from the open list.
    this.setCache(assetId, prev.filter(i => i.id !== issueId), false);
    this.setStatus(issueId, WriteStatuses.Submitting);
    this.toast.show(this.translate.instant('@issueClosingToast'), { classname: 'success' });
    this.issues.requestDisposition(issueId, request).pipe(take(1), takeUntil(this._cancel$)).subscribe({
      // Closed issues drop out of the open list — reconcile = "gone".
      next: () => this.reconcile(assetId, issueId, list => !list.some(i => i.id === issueId)),
      error: err => this.rollback(assetId, prev, issueId, err),
    });
  }

  // Settle the list after a 202 operator report. Nothing is applied optimistically
  // — the writer decides whether the report merges into an open issue or mints a
  // new one, and we cannot fabricate either. The report's requestId becomes the
  // contribution id, so its arrival anywhere in the vehicle's list is the signal
  // that the write landed; if it never shows inside the backoff, fall back to a
  // plain reload so the list is at least current.
  reconcileCreate(assetId: string, requestId: string): void {
    if (!assetId || !requestId) return;
    this.toast.show(this.translate.instant('@issueReportSubmittedToast'), { classname: 'success' });
    this.pollUntil(
      assetId,
      list => list.some(i => (i.contributions ?? []).some(c => c.contributionId === requestId)),
      // The poll gave up, so the cache is the stale thing being corrected — this one has
      // to go to the server.
      met => { if (!met) this.load(assetId, true); },
    );
  }

  // Wipe everything on tenant switch / sign-out and cancel in-flight reconciles.
  clearAll(): void {
    this._cancel$.next();
    this._byAsset.forEach(s => s.next([]));
    this._loadingByAsset.forEach(s => s.next(false));
    this._errorByAsset.forEach(s => s.next(null));
    this._assetByIssue.clear();
    // The cache is gone, so the next caller must fetch rather than adopt an empty list
    // as the answer.
    this.loadedAtMs.clear();
    this._writeStatus.next({});
  }

  // --- Internals -----------------------------------------------------------

  // Refetch with backoff until `expectation` holds. When met, adopt server
  // truth and clear the indicator. If never met, keep the optimistic state and
  // leave the row in 'confirming' for the next load to settle.
  private reconcile(assetId: string, issueId: string, expectation: (list: IssueDto[]) => boolean): void {
    this.pollUntil(assetId, expectation, met => this.setStatus(issueId, met ? WriteStatuses.Idle : WriteStatuses.Confirming));
  }

  // Refetch with backoff until `expectation` holds, adopting server truth when it
  // does. `settled` reports whether it ever held so each caller can decide what a
  // timed-out write means for its own indicator.
  private pollUntil(
    assetId: string,
    expectation: (list: IssueDto[]) => boolean,
    settled: (met: boolean) => void,
    attempt = 0,
  ): void {
    if (attempt >= RECONCILE_DELAYS_MS.length) {
      settled(false);
      return;
    }
    timer(RECONCILE_DELAYS_MS[attempt]).pipe(
      switchMap(() => this.issues.listByAsset(assetId).pipe(catchError(() => of(null)))),
      take(1),
      takeUntil(this._cancel$),
    ).subscribe(list => {
      if (list && expectation(list)) {
        this.setCache(assetId, list);
        settled(true);
      } else {
        this.pollUntil(assetId, expectation, settled, attempt + 1);
      }
    });
  }

  private rollback(assetId: string, prev: IssueDto[], issueId: string, err: unknown): void {
    this.setCache(assetId, prev, false);
    this.setStatus(issueId, WriteStatuses.Failed);
    this.toast.show(this.extractMessage(err) ?? this.translate.instant('@issueWriteFailed'), { classname: 'error' });
  }

  // `reindex` rebuilds the issueId->assetId map; skip it for optimistic edits
  // that don't add/remove issues so a transient edit can't lose the mapping.
  private setCache(assetId: string, list: IssueDto[], reindex: boolean = true): void {
    if (reindex) {
      for (const i of list) this._assetByIssue.set(i.id, assetId);
    }
    this.assetSubject(assetId).next(list);
  }

  private setStatus(issueId: string, status: WriteStatus): void {
    this._writeStatus.next({ ...this._writeStatus.value, [issueId]: status });
  }

  private extractMessage(err: any): string | null {
    const body = err?.error;
    if (Array.isArray(body) && body.length) return body.join(' ');
    if (typeof body === 'string' && body) return body;
    return null;
  }

  private assetSubject(assetId: string): BehaviorSubject<IssueDto[]> {
    let s = this._byAsset.get(assetId);
    if (!s) { s = new BehaviorSubject<IssueDto[]>([]); this._byAsset.set(assetId, s); }
    return s;
  }

  private loadingSubject(assetId: string): BehaviorSubject<boolean> {
    let s = this._loadingByAsset.get(assetId);
    if (!s) { s = new BehaviorSubject<boolean>(false); this._loadingByAsset.set(assetId, s); }
    return s;
  }

  private errorSubject(assetId: string): BehaviorSubject<string | null> {
    let s = this._errorByAsset.get(assetId);
    if (!s) { s = new BehaviorSubject<string | null>(null); this._errorByAsset.set(assetId, s); }
    return s;
  }
}
